import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import ReactCountryFlag from "react-country-flag";
import { api } from "@/lib/api";
import { useDebouncedValue } from "@/lib/use-debounced-value";

type AthleteOption = {
  id: string;
  fullName: string;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  countryIso2?: string | null;
  gender?: string | null;
  resultCount: number;
};

interface AthleteSearchComboboxProps {
  label: string;
  value: AthleteOption | null;
  onSelect: (athlete: AthleteOption) => void;
  excludeId?: string;
  className?: string;
}

export function AthleteSearchCombobox({
  label,
  value,
  onSelect,
  excludeId,
  className,
}: AthleteSearchComboboxProps) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debouncedQuery = useDebouncedValue(query, 300);

  const { data: results, isLoading } = useQuery({
    queryKey: ["compare-athlete-search", debouncedQuery],
    queryFn: async () => {
      const { data, error } = await api.GET("/search/athletes", {
        params: { query: { q: debouncedQuery, limit: 8, offset: 0 } },
      });
      if (error) throw error;
      return data;
    },
    enabled: debouncedQuery.length >= 2,
  });

  const filtered = (results ?? []).filter(
    (a: AthleteOption) => a.id !== excludeId,
  );

  const handleSelect = (athlete: AthleteOption) => {
    onSelect(athlete);
    setQuery("");
    setOpen(false);
  };

  const handleClear = () => {
    onSelect(null as any);
    setQuery("");
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleBlur = (e: React.FocusEvent) => {
    if (!containerRef.current?.contains(e.relatedTarget as Node)) {
      setOpen(false);
    }
  };

  if (value) {
    return (
      <div className={`compare-search-combobox ${className ?? ""}`}>
        <span className="compare-search-label">{label}</span>
        <button
          type="button"
          className="compare-search-selected"
          onClick={handleClear}
        >
          {value.countryIso2 && (
            <ReactCountryFlag
              countryCode={value.countryIso2}
              svg
              style={{ width: "1em", height: "1em", marginRight: "0.35em" }}
              title={value.country ?? value.countryIso2}
            />
          )}
          <span className="compare-search-selected-name">{value.fullName}</span>
          <span className="compare-search-selected-clear">&times;</span>
        </button>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`compare-search-combobox ${className ?? ""}`}
      onBlur={handleBlur}
    >
      <span className="compare-search-label">{label}</span>
      <input
        ref={inputRef}
        type="text"
        className="compare-search-input"
        placeholder="Search by name..."
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
      />
      {open && debouncedQuery.length >= 2 && (
        <div className="compare-search-dropdown">
          {isLoading && (
            <div className="compare-search-dropdown-message">Searching...</div>
          )}
          {!isLoading && filtered.length === 0 && (
            <div className="compare-search-dropdown-message">
              No athletes found
            </div>
          )}
          {filtered.map((athlete: AthleteOption) => {
            const location = [athlete.city, athlete.state, athlete.country]
              .filter(Boolean)
              .join(", ");
            return (
              <button
                key={athlete.id}
                type="button"
                className="compare-search-dropdown-item"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(athlete)}
              >
                <div className="compare-search-dropdown-item-main">
                  {athlete.countryIso2 && (
                    <ReactCountryFlag
                      countryCode={athlete.countryIso2}
                      svg
                      style={{
                        width: "1em",
                        height: "1em",
                        marginRight: "0.35em",
                      }}
                      title={athlete.country ?? athlete.countryIso2}
                    />
                  )}
                  <span>{athlete.fullName}</span>
                </div>
                <div className="compare-search-dropdown-item-meta">
                  {location && <span>{location}</span>}
                  <span>{athlete.resultCount} races</span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export type { AthleteOption };
